import React, { useState } from 'react';
import { useLoginModalContext } from '../../context/LoginModalContext';
import { useLoginUser } from '../../api/users';
import { useUserContext } from '../../context/UserContext';
import { useNotifications } from '../../context/NotificationContext';
import { X, Sun } from 'lucide-react';

interface LoginModalPasswordProps {
  onClose: () => void;
  onLogin: (email: string) => void;
}

const LoginModalPassword: React.FC<LoginModalPasswordProps> = ({ 
  onClose,
  onLogin
}) => {
  const { email, setCurrentStep } = useLoginModalContext();
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const loginUserMutation = useLoginUser();
  const { login } = useUserContext();
  const { showError } = useNotifications();

  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (password.trim()) {
      try {
        const result = await loginUserMutation.mutateAsync({ 
          email: email,
          password: password
        });
        login(result.user);
        onLogin(result.user.email);
      } catch (error: any) {
        setErrorMessage('Invalid email or password. Please try again.');
      }
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>

          <div className="sun-icon">
            <Sun size={48} color="var(--primary-color)" />
          </div>

          <h1 className="modal-title">Welcome Back!</h1>
          
          <p className="modal-description">
            Sign in to your account to access your saved camps and schedules.
          </p>
          
          {errorMessage && (
            <div className="message message--error">
              {errorMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Password</label>
              <input
                type="password"
                id="password"
                className="form-input"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="Enter password"
                required
                disabled={loginUserMutation.isPending}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn--primary btn--login"
              disabled={loginUserMutation.isPending || !password.trim()}
            >
              {loginUserMutation.isPending ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <p className="login-link">
            <button 
              type="button" 
              className="login-link-text" 
              onClick={() => setCurrentStep('reset-password')}
            >
              Forgot your password?
            </button>
          </p>
        </div>
      </div>
    </>
  );
};

export default LoginModalPassword;
